import { kioskTransactionsApi, kioskStudentsApi } from '@/services/kioskApi';

// Initial state
const state = {
  selectedTransactionType: null,
  transactionTypes: [],
  student: null,
  isPriority: false,
  priorityCode: '',
  requirementsMet: false,
  queueNumber: null,
  error: null,
  loading: false,
  transactionAmount: 0
};

// Getters
const getters = {
  getSelectedTransactionType: (state) => state.selectedTransactionType,
  getTransactionTypes: (state) => state.transactionTypes,
  getStudent: (state) => state.student,
  isPriorityMode: (state) => state.isPriority,
  getPriorityCode: (state) => state.priorityCode,
  areRequirementsMet: (state) => state.requirementsMet,
  getQueueNumber: (state) => state.queueNumber,
  hasError: (state) => state.error !== null,
  getError: (state) => state.error,
  isLoading: (state) => state.loading,
  getTransactionAmount: (state) => state.transactionAmount
};

// Actions
const actions = {
  async fetchTransactionTypes({ commit }) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      const response = await kioskTransactionsApi.getActiveTypes();
      const transactionTypes = response.data;
      
      // Fetch requirements for each transaction type
      for (const type of transactionTypes) {
        try {
          // Parse flow_config if it's a string - KEEP THIS for proper flow handling
          if (typeof type.flow_config === 'string' && type.flow_config !== '') {
            try {
              type.flow_config = JSON.parse(type.flow_config);
            } catch (parseError) {
              console.error(`Error parsing flow_config for transaction type ${type.id}:`, parseError);
              type.flow_config = null;
            }
          }
          
          // Standard requirements fetching - unchanged from original
          const requirementsResponse = await kioskTransactionsApi.getTypeRequirements(type.id);
          // Filter only the requirements that should be visible on the kiosk
          type.requirements = requirementsResponse.data.filter(req => req.visible_on_kiosk);
        } catch (error) {
          console.error(`Failed to load requirements for transaction type ${type.id}:`, error);
          type.requirements = [];
        }
      }
      
      commit('SET_TRANSACTION_TYPES', transactionTypes);
      
      return transactionTypes;
    } catch (error) {
      commit('SET_ERROR', 'Failed to load transaction types.');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  selectTransactionType({ commit }, transactionType) {
    // Keep flow_config parsing for proper flow handling
    if (transactionType && typeof transactionType.flow_config === 'string' && transactionType.flow_config !== '') {
      try {
        transactionType.flow_config = JSON.parse(transactionType.flow_config);
      } catch (error) {
        console.error('Error parsing flow_config in selectTransactionType:', error);
        transactionType.flow_config = null;
      }
    }
    
    commit('SET_SELECTED_TRANSACTION_TYPE', transactionType);
  },
  
  async fetchStudentByIdNumber({ commit }, idNumber) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      // Call API to get student info by ID number
      const response = await kioskStudentsApi.getStudentByIdNumber(idNumber);
      
      if (response.data) {
        commit('SET_STUDENT', response.data);
        return response.data;
      } else {
        throw new Error('Student not found.');
      }
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || error.message || 'Failed to load student information.');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  setPriorityMode({ commit }, { isPriority, priorityCode = '' }) {
    commit('SET_PRIORITY_MODE', isPriority);
    commit('SET_PRIORITY_CODE', priorityCode);
  },
  
  setRequirementsMet({ commit }, requirementsMet) {
    commit('SET_REQUIREMENTS_MET', requirementsMet);
  },
  
  setTransactionAmount({ commit }, amount) {
    commit('SET_TRANSACTION_AMOUNT', amount);
  },
  
  async createTransaction({ state, commit }) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      if (!state.selectedTransactionType) {
        throw new Error('Missing transaction type information.');
      }
      
      if (!state.requirementsMet) {
        throw new Error('Please confirm all requirements are met.');
      }
      
      // Create transaction data object
      const transactionData = {
        student_id: state.student ? state.student.id : null,
        transaction_type_id: state.selectedTransactionType.id,
        is_priority: state.isPriority,
        amount: state.transactionAmount
      };
      
      // Add priority code if present
      if (state.isPriority && state.priorityCode) {
        transactionData.priority_code = state.priorityCode;
      }
      
      // Create transaction
      const response = await kioskTransactionsApi.createTransaction(transactionData);
      
      // Set queue number from response
      if (response.data && response.data.queue_number) {
        commit('SET_QUEUE_NUMBER', response.data.queue_number);
      }
      
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Failed to create transaction.');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  async updateTransaction({ commit }, { transactionId, amount }) {
    try {
      const response = await kioskTransactionsApi.updateTransaction(transactionId, { amount });
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Failed to update transaction.');
      throw error;
    }
  },
  
  async printTicket({ commit }, { transactionId, printerName }) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      // Call API to print ticket
      // If printerName is provided, pass it to the API
      const response = printerName 
        ? await kioskTransactionsApi.printTicket(transactionId, printerName)
        : await kioskTransactionsApi.printTicket(transactionId);
      
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to print ticket.');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  resetKiosk({ commit, dispatch }) {
    commit('RESET_STATE');
    dispatch('clearSessionData');
  },
  
  clearSessionData() {
    // Clear all kiosk-related session storage data
    sessionStorage.removeItem('kiosk_priority_enabled');
    sessionStorage.removeItem('kiosk_priority_code');
    sessionStorage.removeItem('kiosk_priority_verified');
    sessionStorage.removeItem('kiosk_student_id');
  }
};

// Mutations
const mutations = {
  SET_TRANSACTION_TYPES(state, transactionTypes) {
    state.transactionTypes = transactionTypes;
  },
  
  SET_SELECTED_TRANSACTION_TYPE(state, transactionType) {
    state.selectedTransactionType = transactionType;
  },
  
  SET_STUDENT(state, student) {
    state.student = student;
  },
  
  SET_PRIORITY_MODE(state, isPriority) {
    state.isPriority = isPriority;
  },
  
  SET_PRIORITY_CODE(state, priorityCode) {
    state.priorityCode = priorityCode;
  },
  
  SET_REQUIREMENTS_MET(state, requirementsMet) {
    state.requirementsMet = requirementsMet;
  },
  
  SET_QUEUE_NUMBER(state, queueNumber) {
    state.queueNumber = queueNumber;
  },
  
  SET_ERROR(state, error) {
    state.error = error;
  },
  
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  
  SET_TRANSACTION_AMOUNT(state, amount) {
    state.transactionAmount = amount;
  },
  
  RESET_STATE(state) {
    state.selectedTransactionType = null;
    state.student = null;
    state.isPriority = false;
    state.priorityCode = '';
    state.requirementsMet = false;
    state.queueNumber = null;
    state.error = null;
    state.transactionAmount = 0;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}; 